import React, { useState, useEffect } from 'react'
import QRCode from 'react-qr-code'
import {
  generateOTPAuthUrl,
  generateSecret,
  verifyTOTP,
  generateRecoveryCodes
} from '../../hooks/totpUtils'
import { useFirebaseCRUD } from '../../hooks/useFirebaseCRUD'
import Button from '../Button'
import { useNavigate } from 'react-router-dom'
import { signOut } from 'firebase/auth'
import { auth, db } from '../../firebase'
import Spinner from '../Spinner'
import { doc, getDoc } from 'firebase/firestore'
import { toast } from 'react-hot-toast'
import MFALearningModal from '../MFALearningModal'
import { ClipboardIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import { Copy } from 'lucide-react'
import { Tooltip } from 'react-tooltip'

export default function MFASetup() {
  const navigate = useNavigate()
  const [userData, setUserData] = useState(null)
  const [secret, setSecret] = useState(null)
  const [otp, setOtp] = useState('')
  const [verified, setVerified] = useState(false)
  const [recoveryCodes, setRecoveryCodes] = useState([])
  const [error, setError] = useState('')
  const [loadingSecret, setLoadingSecret] = useState(true)
  const [loadingAction, setLoadingAction] = useState(false)
  const [showLearnModal, setShowLearnModal] = useState(false)

  const { updateDocument } = useFirebaseCRUD()

  // 🔹 Fetch logged-in user's Firestore document
  useEffect(() => {
    let intervalId

    const fetchUser = async () => {
      try {
        const currentUser = auth.currentUser
        if (!currentUser) return

        const userDocRef = doc(db, 'users', currentUser.uid)
        const userSnap = await getDoc(userDocRef)
        if (userSnap.exists()) {
          setUserData({ id: currentUser.uid, ...userSnap.data() })
          clearInterval(intervalId)
        } else {
          console.error('User document not found')
          setError('User data not found')
        }
      } catch (err) {
        console.error('Error fetching user document:', err)
        setError('Failed to fetch user data')
      }
    }

    fetchUser()

    intervalId = setInterval(() => {
      if (!userData) fetchUser()
    }, 1000)

    return () => clearInterval(intervalId)
  }, [userData])

  // 🔹 Generate MFA secret once userData is loaded
  useEffect(() => {
    if (userData?.email && !secret && loadingSecret) {
      try {
        const s = generateSecret()
        setSecret({
          base32: s,
          otpauth_url: generateOTPAuthUrl(s, userData.email, 'LiveAlarm-V2')
        })
      } catch (err) {
        console.error(err)
        setError('Failed to generate MFA secret')
      } finally {
        setLoadingSecret(false)
      }
    }
  }, [userData, secret, loadingSecret])

  const verifyOtp = async (e) => {
    if (e) e.preventDefault()
    if (otp.length !== 6) return toast.error('Enter a valid 6-digit code')

    autoVerifyOtp(otp)
  }

  const enableMFA = async () => {
    setLoadingAction(true)
    try {
      const success = await updateDocument(`users/${userData.id}`, {
        isTwoFactorEnabled: true,
        secretKey: secret.base32,
        twoFactorRecoveryCodes: recoveryCodes,
        updatedAt: new Date().toISOString()
      })
      if (success) {
        localStorage.removeItem('mfaInProgress')
        localStorage.removeItem('mfaUserId')
        await signOut(auth)
        navigate('/login')
      } else {
        toast.error('Failed to enable MFA')
      }
    } catch (err) {
      console.error(err)
      toast.error('Failed to enable MFA')
    } finally {
      setLoadingAction(false)
    }
  }

  const handleCancel = async () => {
    await signOut(auth)
    localStorage.removeItem('mfaInProgress')
    localStorage.removeItem('mfaUserId')
    navigate('/login')
  }

  const autoVerifyOtp = async (enteredOtp) => {
    setLoadingAction(true)
    try {
      const valid = await verifyTOTP(secret.base32, enteredOtp)
      if (!valid) {
        toast.error('Invalid OTP', { id: 'otp-error' })
        return
      }

      // OTP is valid — navigate or show recovery codes
      const codes = generateRecoveryCodes()
      setRecoveryCodes(codes)
      setVerified(true)
      setError('')

      // Optional: direct navigation if you want
      // navigate('/some-path-after-success')
    } catch (err) {
      console.error(err)
      toast.error('Verification failed')
    } finally {
      setLoadingAction(false)
    }
  }

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return

    const newOtp = otp.split('')
    newOtp[index] = value
    const updatedOtp = newOtp.join('')
    setOtp(updatedOtp)
    setError('')

    // Focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-input-${index + 1}`)
      if (nextInput) nextInput.focus()
    }

    // Automatically verify when 6 digits entered
    if (updatedOtp.length === 6 && /^[0-9]{6}$/.test(updatedOtp)) {
      autoVerifyOtp(updatedOtp)
    }
  }

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-input-${index - 1}`)
      if (prevInput) prevInput.focus()
    }
  }

  const handleDownloadQRCode = () => {
    const qrElement = document.getElementById('qr-code-container')
    if (!qrElement) return

    const svg = qrElement.querySelector('svg')
    if (!svg) return

    const svgData = new XMLSerializer().serializeToString(svg)
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(svgBlob)

    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.width * 4
      canvas.height = img.height * 4
      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

      const pngUrl = canvas.toDataURL('image/png')
      const link = document.createElement('a')
      link.href = pngUrl
      link.download = 'mfa-qr-code.png'
      link.click()

      URL.revokeObjectURL(url)
    }
    img.src = url
  }

  const generateRecoveryCodesFilename = () => {
    if (!userData?.email) return 'Live-Alarm-recovery-codes.txt'

    const email = userData.email
    const currentDate = new Date()
    const day = String(currentDate.getDate()).padStart(2, '0')
    const month = String(currentDate.getMonth() + 1).padStart(2, '0')
    const year = currentDate.getFullYear()

    return `Live-Alarm-recovery-codes-${email}-${day}-${month}-${year}.txt`
  }

  if (!userData || loadingSecret) {
    return (
      <div className='login-container'>
        <div className='login-grid'>
          <div className='login-left login-centered'>
            <div className='login-form'>
              <Spinner />
              <p className='mt-2 text-gray-500'>Preparing MFA setup...</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!secret && error) {
    return (
      <div className='login-container'>
        <div className='login-grid'>
          <div className='login-left login-centered'>
            <div className='login-form'>
              <h2 className='text-xl font-bold mb-4'>MFA Setup Failed</h2>
              <p className='mb-4 text-red-500'>
                {error || 'Failed to generate MFA secret'}
              </p>
              <Button onClick={handleCancel} className='login-button'>
                Back to Login
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='login-container'>
      <div className='login-grid'>
        <div className='login-left login-centered'>
          <div className='login-form'>
            {!verified ? (
              <>
                <div className='mb-4'>
                  <h2 className='!text-[24px] !font-bold mb-4 flex items-center gap-2'>
                    Enable Two-Factor Authentications
                    {!verified && (
                      <div>
                        <div className=''>
                          <h5
                            className='inline-block my-2 '
                            data-tooltip-id='tips-tooltip'
                            data-tooltip-place='right' // Add this for left alignment
                          >
                            <img
                              className='info-icon cursor-pointer'
                              src='/assets/images/info.png'
                              alt='signup visual'
                            />
                          </h5>
                          <Tooltip
                            id='tips-tooltip'
                            place='right'
                            effect='solid'>
                            <div className='max-w-xs text-sm'>
                              <ul className='list-disc list-inside mt-1'>
                                <li>
                                  Keep this page open while you set up the app.
                                </li>
                                <li>
                                  Switching between your phone and computer will
                                  not reset progress.
                                </li>
                                <li>
                                  If setup doesn’t work, delete the entry in
                                  your authenticator app and try scanning or
                                  entering the key again.
                                </li>
                              </ul>
                            </div>
                          </Tooltip>
                        </div>
                      </div>
                    )}
                  </h2>
                  <h5>Step 1 — Open or Install an Authenticator App</h5>
                  <ul className='mb-4'>
                    <li className='mb-3'>
                      On your phone, open your authenticator app (for  example,<span className='bold'> Duo
                        Mobile, Google Authenticator,</span> or <span className='bold'>Microsoft Authenticator</span>).
                      <br />
                      Don’t have one? Install one and open it.
                    </li>
                  </ul>
                  <h5>Step 2 — Add a New Account</h5>
                  <ul>
                    <li>
                      In your authenticator app, tap <span className='bold'>+ Add account</span> or
                      <span className='bold'> Set up account</span> and choose <span className='bold'>QR Code</span>
                      <form onSubmit={verifyOtp}>
                        {/* QR Code and Secret Key */}
                        <div className='mb-4'>
                          {/* QR Code */}
                          <div className='flex items-center justify-start gap-2 mt-3 mb-2'>
                            <h6 className='!text-[#333]'>Scan QR Code from authenticator apps</h6>
                          </div>
                          <div
                            id='qr-code-container'
                            className='mb-4 p-2 bg-white rounded flex justify-center'>
                            <QRCode
                              value={secret.otpauth_url}
                              size={150}
                              level='H'
                            />
                          </div>

                          {/* OR separator */}
                          <div className='divider-box text-center mb-4 text-gray-500 font-medium'>
                            or
                          </div>

                          {/* Secret Key */}
                          <div className='mb-4'>
                            <h6 className='!text-[#333]'>Enter Setup Key manually</h6>
                            <ul>
                              <li>
                                If you can’t scan the QR Code, Enter the Setup
                                Key Manually in authenticator app.
                              </li>
                            </ul>
                            <div className='relative bg-gray-100 rounded p-2 font-mono text-gray-800 flex items-center mt-2'>
                              <span className='break-all'>{secret.base32}</span>
                              <button
                                type='button'
                                onClick={() => {
                                  navigator.clipboard.writeText(secret.base32)
                                  toast.success(
                                    'Secret key copied to clipboard'
                                  )
                                }}
                                className='absolute right-2 text-gray-600 hover:text-gray-800'>
                                {/* <Copy className='w-5 h-5' /> */}
                              </button>
                            </div>
                            <ul>
                              <li>
                                Your app will now show 6-digit codes that
                                refresh every 30 seconds.
                              </li>
                            </ul>
                          </div>
                        </div>

                        {/* <p className='mb-4 text-sm text-gray-600'>
                  <button
                    type='button'
                    onClick={() => setShowLearnModal(true)}
                    className='text-blue-600 hover:text-blue-800 hover:underline font-medium cursor-pointer'>
                    Learn how MFA works
                  </button>
                </p> */}

                        <MFALearningModal
                          isOpen={showLearnModal}
                          onClose={() => setShowLearnModal(false)}
                        />
                      </form>
                    </li>
                  </ul>
                </div>
                {/* OTP Inputs */}
                <div className='mb-4'>
                  <h5>Step 3 — Verify your Code</h5>
                  <form onSubmit={verifyOtp}>
                    <ul className='mb-4'>
                      <li>
                        <span className='bold'>Enter the current 6-digit code</span> from your authenticator
                        app. If it changes before you finish, just enter the
                        newest one.
                        <h6 className='block my-2 !text-[#333]'>Enter verification code:</h6>
                        <div className='flex gap-3 justify-start'>
                          {[...Array(6)].map((_, index) => (
                            <input
                              key={index}
                              id={`otp-input-${index}`}
                              type='text'
                              value={otp[index] || ''}
                              onChange={(e) =>
                                handleOtpChange(index, e.target.value)
                              }
                              onKeyDown={(e) => handleOtpKeyDown(index, e)}
                              className='w-10 h-10 text-center text-lg border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500'
                              maxLength={1}
                              inputMode='numeric'
                              pattern='\d*'
                              disabled={loadingAction}
                            />
                          ))}
                        </div>
                      </li>
                    </ul>
                    <div className='flex items-center gap-4'>
                      <Button
                        onClick={handleCancel}
                        className='login-button !bg-gray-500 !text-white'>
                        Cancel
                      </Button>
                      <Button
                        type='submit'
                        disabled={loadingAction || otp.length !== 6}
                        className='login-button'>
                        {loadingAction ? 'Verifying...' : 'Verify'}
                      </Button>
                    </div>
                  </form>
                </div>
              </>
            ) : (
              <>
                <h5 className='mb-4'>Save These Codes</h5>
                <div className='mb-4 p-4 bg-gray-100 rounded'>
                  {recoveryCodes.map((item, index) => (
                    <div key={index} className='mb-1 font-mono'>
                      {item.code}
                    </div>
                  ))}
                </div>

                <div className='flex gap-2 mb-4'>
                  <Button
                    onClick={() => {
                      const codesText = recoveryCodes
                        .map((item) => item.code)
                        .join('\n')
                      const blob = new Blob([codesText], { type: 'text/plain' })
                      const url = URL.createObjectURL(blob)
                      const link = document.createElement('a')
                      link.href = url
                      link.download = generateRecoveryCodesFilename()
                      link.click()
                      URL.revokeObjectURL(url)
                    }}
                    className='login-button flex items-center gap-2'>
                    Download Recovery Codes
                  </Button>

                  <Button
                    onClick={enableMFA}
                    disabled={loadingAction}
                    className='login-button'>
                    {loadingAction
                      ? 'Enabling MFA...'
                      : 'Enable 2FA & Finish Setup'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>

        <div className='login-right login-centered login-overflow'>
          <img
            className='login-image'
            src='/assets/images/logo.png'
            alt='MFA Setup Illustration'
          />
        </div>
      </div>
    </div>
  )
}
